import {RouteDefine} from "../../routes/RouteDefine";
import {SetupInterface} from "../../interfaces/private/SetupInterface";
import {getSyncEngineVersion} from "./syncEngine";
import {
    ALL_SYNC_MODULES,
    SyncModuleKey,
} from "../../features/public/SetupV2/helpers/onboardingModules";
import {
    getEffectiveSyncModules,
    usesCustomerBalanceLedger,
} from "./setupCompleteness";

export type SyncNavItem = {
    key: SyncModuleKey;
    route: string;
    labelKey: string;
};

export const SYNC_NAV_ITEMS: SyncNavItem[] = [
    {key: "invoice", route: RouteDefine.invoice, labelKey: "common.navbar.invoices"},
    {key: "refund", route: RouteDefine.refund, labelKey: "common.navbar.refunds"},
    {key: "payment", route: RouteDefine.payment, labelKey: "common.navbar.payments"},
    {key: "payout", route: RouteDefine.payout, labelKey: "common.navbar.payouts"},
    {key: "subscription", route: RouteDefine.subscription, labelKey: "common.navbar.subscriptions"},
];

/** Menu order when ledger is on — operational modules first (Invoice sync is skipped). */
const LEDGER_NAV_ORDER: SyncModuleKey[] = [
    "payment",
    "payout",
    "refund",
    "subscription",
];

export const getVisibleSyncNavItems = (setup: SetupInterface): SyncNavItem[] => {
    if (getSyncEngineVersion() !== "v2") {
        // Refund is V2-only — never show on V1 nav.
        return SYNC_NAV_ITEMS.filter((item) => item.key !== "refund");
    }

    const enabled = getEffectiveSyncModules(setup);
    let items = SYNC_NAV_ITEMS.filter((item) => enabled.includes(item.key));

    if (usesCustomerBalanceLedger(setup)) {
        // Invoice sync is skipped in ledger — hide from Integration management menu.
        // /invoice remains reachable (dashboard log link / bookmarks) via SyncNavGuard.
        items = items.filter((item) => item.key !== "invoice");
        items.sort(
            (a, b) => LEDGER_NAV_ORDER.indexOf(a.key) - LEDGER_NAV_ORDER.indexOf(b.key),
        );
    }

    return items;
};

export const isSyncNavRoute = (pathname: string): boolean =>
    SYNC_NAV_ITEMS.some((item) => item.route === pathname);

export const isSyncNavRouteVisible = (pathname: string, setup: SetupInterface): boolean => {
    if (!isSyncNavRoute(pathname)) {
        return true;
    }
    const item = SYNC_NAV_ITEMS.find((i) => i.route === pathname);
    if (!item) {
        return true;
    }
    if (getSyncEngineVersion() !== "v2") {
        // Refund route is V2-only.
        return item.key !== "refund";
    }
    return getEffectiveSyncModules(setup).includes(item.key);
};

/** Fallback when current log route is hidden (e.g. module disabled). */
export const getDefaultSyncNavRoute = (setup: SetupInterface): string => {
    const visible = getVisibleSyncNavItems(setup);
    return visible[0]?.route ?? RouteDefine.dashboard;
};

export const ALL_SYNC_MODULE_KEYS: SyncModuleKey[] = [...ALL_SYNC_MODULES];
